import {useMemo, useState} from "react";
import {useApp} from "../../app.context";
import {OrderManifestEntry} from "../../models/attributeManifest.model";
import ImagePicker from "../general/imagePicker.view";

// Editor section for assigning per-order images. Only orders that actually have
// a configurable image are shown — i.e. those with an IconKey (the small
// info-bar strip glyph, ui.icon.*) and/or a ButtonImageKey (the large action-
// button art, ui.button.*). Building/tile orders have neither (their visual is
// the target entity's own image) and are omitted. Orders that have an icon but
// no button show an "N/A" placeholder in the button slot. Click an image to
// open the shared ImagePicker (upload, or pick an existing/archived image),
// which writes straight to the order's key (the 'imageKey' target).
function OrdersEditor() {
    const {attributeManifest} = useApp();
    const [picking, setPicking] = useState<string | undefined>(undefined);

    const orders = useMemo(
        () => (attributeManifest?.Orders ?? [])
            .filter(o => o.IconKey || o.ButtonImageKey)
            .sort((a, b) => a.TypeName.localeCompare(b.TypeName)),
        [attributeManifest],
    );

    return (
        <div className={'layout vertical flex-grow'} style={{padding: 16, overflowY: 'auto'}}>
            {orders.length === 0
                ? <p className={'muted-text'}>
                    No orders in the attribute manifest — re-export it from the Unity client (dev console <code>eam</code>).
                  </p>
                : orders.map(order => <OrderRow key={order.TypeName} order={order} onPick={setPicking}/>)}

            {picking !== undefined &&
                <ImagePicker target={{kind: 'imageKey', key: picking}} currentKey={picking}
                             onClose={() => setPicking(undefined)}/>}
        </div>
    );
}

function OrderRow({order, onPick}: { order: OrderManifestEntry; onPick: (key: string) => void }) {
    const name = order.TypeName.endsWith('Order')
        ? order.TypeName.slice(0, -'Order'.length)
        : order.TypeName;

    return (
        <div className={'layout horizontal'} style={{
            alignItems: 'center', gap: 24, padding: '12px 0',
            borderBottom: 'solid 1px var(--overlay-color)',
        }}>
            <p className={'text header'} style={{width: 160, flexShrink: 0, fontSize: 20}}>{name}</p>
            {order.IconKey && <ClickableImage imageKey={order.IconKey} size={40} onPick={onPick}/>}
            {order.ButtonImageKey
                ? <ClickableImage imageKey={order.ButtonImageKey} size={128} onPick={onPick}/>
                : <NaPanel size={128}/>}
        </div>
    );
}

// Bare image that opens the ImagePicker for its key on click.
function ClickableImage({imageKey, size, onPick}: { imageKey: string; size: number; onPick: (key: string) => void }) {
    const {getImageUrl} = useApp();
    return (
        <img src={getImageUrl(imageKey)} alt={imageKey}
             title={`${imageKey} — click to choose`}
             onClick={() => onPick(imageKey)}
             style={{width: size, height: size, objectFit: 'contain', imageRendering: 'pixelated', cursor: 'pointer'}}/>
    );
}

// Placeholder shown in the button slot for orders that have no action button.
function NaPanel({size}: { size: number }) {
    return (
        <div className={'layout horizontal center'} style={{
            width: size, height: size,
            border: 'solid 1px var(--overlay-color)', borderRadius: 4,
            color: 'rgba(255,255,255,0.4)', fontSize: 14, userSelect: 'none',
        }}>
            N/A
        </div>
    );
}

export default OrdersEditor;
